<script lang="ts">
    import type { Snippet } from 'svelte'
    import type { ClassValue } from 'svelte/elements'
    import type { IconGraphic } from '../foundations/iconography.js'
    import { ArrowChevronDownIcon, ArrowChevronUpIcon } from '../icons.js'
    import Icon from './Icon.svelte'
    import Spinner from './Spinner.svelte'
    import {
        ButtonBoxTone,
        ButtonPrimaryDisabledTone,
        ButtonPrimaryTone,
        ButtonSecondaryDisabledTone,
        ButtonSecondaryTone,
        getButtonIconSize,
        type ButtonTone
    } from './button-config.js'

    export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'boxless' | 'box'
    export type ButtonSize = 'large' | 'small'
    export type ButtonShape = 'pill' | 'rounded'
    export type ButtonBackground = 'white' | 'gray'

    type NativeInteractiveAttributes = Record<string, unknown>

    export type ButtonProps = NativeInteractiveAttributes & {
        active?: boolean
        background?: ButtonBackground
        children?: Snippet
        class?: ClassValue
        disabled?: boolean
        download?: string
        disclosure?: boolean
        href?: string
        label?: string
        leadingIcon?: IconGraphic
        loading?: boolean
        rel?: string
        shape?: ButtonShape
        size?: ButtonSize
        target?: '_blank' | '_parent' | '_self' | '_top'
        trailingIcon?: IconGraphic
        type?: 'button' | 'submit' | 'reset'
        variant?: ButtonVariant
    }

    const BaseClass =
        'font-ts-sans inline-flex shrink-0 cursor-pointer items-center justify-center border border-solid whitespace-nowrap transition-colors disabled:cursor-not-allowed'
    const SizeClass: Record<ButtonSize, string> = {
        large: 'ts-paragraph-large-semibold min-h-11 gap-2 px-8 py-3',
        small: 'ts-paragraph-medium-semibold min-h-8 gap-1.5 px-6 py-1.5'
    }
    const TertiarySizeClass: Record<ButtonSize, string> = {
        large: 'ts-paragraph-small-regular min-h-10 gap-1.5 px-3 py-3',
        small: 'ts-paragraph-small-regular min-h-10 gap-1.5 px-3 py-3'
    }
    const BoxlessSizeClass: Record<ButtonSize, string> = {
        large: 'ts-label-medium-bold gap-1.5 px-3 py-1',
        small: 'ts-label-medium-bold gap-1.5 px-3 py-1'
    }
    const BoxSizeClass = 'min-h-12 gap-2 px-[17px] py-[13px] text-sm/5 font-semibold'
    const ShapeClass: Record<ButtonShape, string> = {
        pill: 'rounded-full',
        rounded: 'rounded-[4px]'
    }
    const FocusClass: Record<ButtonVariant, Record<ButtonSize, string>> = {
        box: {
            large: 'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-ts-primary-main',
            small: 'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-ts-primary-main'
        },
        boxless: {
            large: 'focus-visible:outline-none',
            small: 'focus-visible:outline-none'
        },
        primary: {
            large: 'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-ts-primary-main',
            small: 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ts-primary-main'
        },
        secondary: {
            large: 'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-ts-primary-main',
            small: 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ts-primary-main'
        },
        tertiary: {
            large: 'focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-ts-primary-main',
            small: 'focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-ts-primary-main'
        }
    }

    let {
        active = false,
        background = 'white',
        children,
        class: className,
        disabled = false,
        download,
        disclosure = false,
        href,
        label,
        leadingIcon,
        loading = false,
        rel,
        shape = 'pill',
        size = 'large',
        target,
        trailingIcon,
        type = 'button',
        variant = 'primary',
        ...interactiveAttributes
    }: ButtonProps = $props()

    const isAnchor = $derived(href !== undefined)
    const isDisabled = $derived(disabled || loading)
    const resolvedHref = $derived(isDisabled ? undefined : href)
    const resolvedTrailingIcon = $derived(
        disclosure ? (active ? ArrowChevronUpIcon : ArrowChevronDownIcon) : trailingIcon
    )
    const shouldRenderText = $derived(Boolean(children || label))
    const iconSize = $derived(variant === 'box' ? 'm' : getButtonIconSize(size))
    const iconClass = $derived(variant === 'tertiary' ? 'size-2' : undefined)
    const tone: ButtonTone = $derived(
        resolveTone(variant, active, isDisabled, background, isAnchor)
    )
    const variantSizeClass = $derived(resolveSizeClass(variant, size))
    const resolvedShapeClass = $derived(resolveShapeClass(variant, shape))
    const focusClass = $derived([
        FocusClass[variant][size],
        variant === 'boxless' &&
            (background === 'gray'
                ? 'focus-visible:border-ts-primary-dark'
                : 'focus-visible:border-ts-primary-main')
    ])
    const ariaBusy = $derived(loading ? true : undefined)
    const ariaDisabled = $derived(isAnchor && isDisabled ? true : undefined)
    const ariaExpanded = $derived(disclosure ? active : undefined)
    const ariaPressed = $derived(!disclosure && active ? true : undefined)
    const spinnerBackground = $derived(variant === 'primary' ? 'dark' : 'light')

    function resolveTone(
        currentVariant: ButtonVariant,
        isActive: boolean,
        currentDisabled: boolean,
        currentBackground: ButtonBackground,
        currentIsAnchor: boolean
    ): ButtonTone {
        if (currentVariant === 'box') {
            return currentDisabled
                ? ButtonSecondaryDisabledTone
                : resolveAnchorHoverTone(ButtonBoxTone, currentIsAnchor)
        }

        if (currentVariant === 'primary') {
            return currentDisabled
                ? ButtonPrimaryDisabledTone
                : resolveAnchorHoverTone(ButtonPrimaryTone, currentIsAnchor)
        }

        if (currentVariant === 'secondary') {
            return currentDisabled
                ? ButtonSecondaryDisabledTone
                : resolveAnchorHoverTone(ButtonSecondaryTone, currentIsAnchor)
        }

        if (currentVariant === 'tertiary') {
            return {
                backgroundClass: isActive
                    ? 'bg-ts-gray-000'
                    : resolveHoverClass(
                          currentDisabled,
                          currentIsAnchor,
                          'bg-ts-gray-000 hover:bg-ts-primary-main',
                          'bg-ts-gray-000 enabled:hover:bg-ts-primary-main'
                      ),
                borderClass: currentDisabled
                    ? 'border-ts-gray-500'
                    : isActive
                      ? 'border-ts-primary-main'
                      : resolveHoverClass(
                            currentDisabled,
                            currentIsAnchor,
                            'border-ts-gray-500 hover:border-ts-primary-main',
                            'border-ts-gray-500 enabled:hover:border-ts-primary-main'
                        ),
                textClass: currentDisabled
                    ? 'text-ts-gray-800 opacity-30'
                    : isActive
                      ? 'text-ts-primary-main'
                      : resolveHoverClass(
                            currentDisabled,
                            currentIsAnchor,
                            'text-ts-gray-800 hover:text-ts-text-reversed',
                            'text-ts-gray-800 enabled:hover:text-ts-text-reversed'
                        )
            }
        }

        if (currentVariant === 'boxless') {
            return {
                backgroundClass:
                    currentBackground === 'gray'
                        ? resolveHoverClass(
                              currentDisabled,
                              currentIsAnchor,
                              'bg-transparent hover:bg-ts-gray-000',
                              'bg-transparent enabled:hover:bg-ts-gray-000'
                          )
                        : resolveHoverClass(
                              currentDisabled,
                              currentIsAnchor,
                              'bg-transparent hover:bg-ts-gray-100',
                              'bg-transparent enabled:hover:bg-ts-gray-100'
                          ),
                borderClass: 'border-transparent',
                textClass: resolveBoxlessTextClass(
                    isActive,
                    currentDisabled,
                    currentBackground,
                    currentIsAnchor
                )
            }
        }

        return {
            backgroundClass:
                currentBackground === 'gray'
                    ? resolveHoverClass(
                          currentDisabled,
                          currentIsAnchor,
                          'bg-transparent hover:bg-ts-gray-000',
                          'bg-transparent enabled:hover:bg-ts-gray-000'
                      )
                    : resolveHoverClass(
                          currentDisabled,
                          currentIsAnchor,
                          'bg-transparent hover:bg-ts-gray-100',
                          'bg-transparent enabled:hover:bg-ts-gray-100'
                      ),
            borderClass: 'border-transparent',
            textClass: currentDisabled
                ? 'text-[#b3d7ed]'
                : isActive
                  ? 'text-ts-secondary-main'
                  : 'text-ts-primary-main'
        }
    }

    function resolveShapeClass(
        currentVariant: ButtonVariant,
        currentShape: ButtonShape
    ): string {
        if (currentVariant === 'box') {
            return 'rounded-[10px]'
        }

        if (currentVariant === 'boxless') {
            return 'rounded-full'
        }

        return ShapeClass[currentShape]
    }

    function resolveSizeClass(currentVariant: ButtonVariant, currentSize: ButtonSize): string {
        if (currentVariant === 'box') {
            return BoxSizeClass
        }

        if (currentVariant === 'boxless') {
            return BoxlessSizeClass[currentSize]
        }

        if (currentVariant === 'tertiary') {
            return TertiarySizeClass[currentSize]
        }

        return SizeClass[currentSize]
    }

    function resolveBoxlessTextClass(
        isActive: boolean,
        currentDisabled: boolean,
        currentBackground: ButtonBackground,
        currentIsAnchor: boolean
    ): string {
        if (currentDisabled) {
            return currentBackground === 'gray'
                ? 'text-ts-primary-dark opacity-30'
                : 'text-[#b3d7ed]'
        }

        if (isActive) {
            return currentBackground === 'gray' ? 'text-ts-secondary-main' : 'text-ts-primary-dark'
        }

        if (currentBackground === 'gray') {
            return 'text-ts-primary-dark'
        }

        return resolveHoverClass(
            currentDisabled,
            currentIsAnchor,
            'text-ts-primary-main hover:text-ts-primary-dark',
            'text-ts-primary-main enabled:hover:text-ts-primary-dark'
        )
    }

    function resolveAnchorHoverTone(tone: ButtonTone, currentIsAnchor: boolean): ButtonTone {
        if (!currentIsAnchor) {
            return tone
        }

        return {
            backgroundClass: tone.backgroundClass.replaceAll('enabled:hover:', 'hover:'),
            borderClass: tone.borderClass.replaceAll('enabled:hover:', 'hover:'),
            textClass: tone.textClass.replaceAll('enabled:hover:', 'hover:')
        }
    }

    function resolveHoverClass(
        currentDisabled: boolean,
        currentIsAnchor: boolean,
        anchorClass: string,
        buttonClass: string
    ): string {
        if (currentDisabled) {
            return buttonClass.replaceAll(/(?:^|\s)enabled:hover:\S+/g, '')
        }

        return currentIsAnchor ? anchorClass : buttonClass
    }
</script>

{#snippet content()}
    {#if loading}
        <Spinner background={spinnerBackground} decorative size="small" />
    {:else if leadingIcon}
        <Icon class={iconClass} color="currentColor" graphic={leadingIcon} size={iconSize} />
    {/if}

    {#if shouldRenderText}
        <span class="min-w-0">
            {#if children}
                {@render children()}
            {:else}
                {label}
            {/if}
        </span>
    {/if}

    {#if !loading && resolvedTrailingIcon}
        <Icon class={iconClass} color="currentColor" graphic={resolvedTrailingIcon} size={iconSize} />
    {/if}
{/snippet}

{#if isAnchor}
    <a
        {...interactiveAttributes}
        aria-busy={ariaBusy}
        aria-disabled={ariaDisabled}
        aria-expanded={ariaExpanded}
        class={[
            BaseClass,
            variantSizeClass,
            resolvedShapeClass,
            tone.backgroundClass,
            tone.borderClass,
            tone.textClass,
            focusClass,
            isDisabled && 'pointer-events-none cursor-not-allowed',
            className
        ]}
        data-active={active}
        {download}
        href={resolvedHref}
        {rel}
        {target}
    >
        {@render content()}
    </a>
{:else}
    <button
        {...interactiveAttributes}
        aria-busy={ariaBusy}
        aria-expanded={ariaExpanded}
        aria-pressed={ariaPressed}
        class={[
            BaseClass,
            variantSizeClass,
            resolvedShapeClass,
            tone.backgroundClass,
            tone.borderClass,
            tone.textClass,
            focusClass,
            className
        ]}
        data-active={active}
        disabled={isDisabled}
        {type}
    >
        {@render content()}
    </button>
{/if}
